import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  BellIcon,
  BriefcaseIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  CalendarIcon as CalendarIconSolid,
  MagnifyingGlassIcon as MagnifyingGlassIconSolid,
  BellIcon as BellIconSolid,
  BriefcaseIcon as BriefcaseIconSolid,
  UserIcon as UserIconSolid
} from '@heroicons/react/24/solid';
import jobAPI from '../../services/jobAPI';
import { useAuth } from '../../contexts/AuthContext';
import { isTransportationProvider } from '../../utils/providerUtils';
import { jobNeedsAvailabilityConfirmation } from '../../utils/jobConfirmation';
import { getJobScheduledDateTime } from '../../utils/dateUtils';

const MobileBottomNav = () => {
  const location = useLocation();
  const { profile } = useAuth();
  const isTransport = isTransportationProvider(profile);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!isTransport) {
      loadPendingCount();
      const interval = setInterval(loadPendingCount, 60000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [isTransport]);

  const loadPendingCount = async () => {
    try {
      const response = await jobAPI.getMyJobs({ limit: 100 });
      const jobs = response.data.data.jobs;
      const now = new Date();

      // Count pending actions
      const overdueReports = jobs.filter(job => {
        if (job.status !== 'completed' || job.completion_report_submitted) return false;
        if (!job.completed_at) return false;
        const completedTime = new Date(job.completed_at);
        const hoursSince = (now - completedTime) / (1000 * 60 * 60);
        return hoursSince > 24;
      }).length;

      const needsConfirmation = jobs.filter(job => {
        if (!jobNeedsAvailabilityConfirmation(job)) return false;
        const jobDate = getJobScheduledDateTime(job);
        return jobDate && jobDate > now;
      }).length;

      setPendingCount(overdueReports + needsConfirmation);
    } catch (error) {
      console.error('Error loading pending count:', error);
    }
  };

  const navItems = isTransport
    ? [
        { name: 'Home', path: '/dashboard', icon: HomeIcon, iconSolid: HomeIconSolid },
        { name: 'Find', path: '/trips/find', icon: MagnifyingGlassIcon, iconSolid: MagnifyingGlassIconSolid },
        { name: 'Profile', path: '/profile', icon: UserIcon, iconSolid: UserIconSolid },
      ]
    : [
        { name: 'Home', path: '/dashboard', icon: HomeIcon, iconSolid: HomeIconSolid },
        { name: 'Schedule', path: '/schedule', icon: CalendarIcon, iconSolid: CalendarIconSolid },
        { name: 'Find Jobs', path: '/jobs/search', icon: MagnifyingGlassIcon, iconSolid: MagnifyingGlassIconSolid },
        { name: 'Pending', path: '/pending', icon: BellIcon, iconSolid: BellIconSolid, badge: pendingCount },
        { name: 'My Jobs', path: '/jobs', icon: BriefcaseIcon, iconSolid: BriefcaseIconSolid },
        { name: 'Profile', path: '/profile', icon: UserIcon, iconSolid: UserIconSolid },
      ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 lg:hidden z-50">
      <div className={`grid h-16 ${navItems.length <= 3 ? 'grid-cols-3' : 'grid-cols-6'}`}>
        {navItems.map((item) => {
          const active = isActive(item.path);
          const Icon = active ? item.iconSolid : item.icon;

          return (
            <Link
              key={item.name}
              to={item.path}
              className={`flex flex-col items-center justify-center relative ${
                active
                  ? 'text-ink'
                  : 'text-gray-400 hover:text-gray-700'
              }`}
            >
              <div className="relative">
                <Icon className="h-6 w-6" />
                {item.badge > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </div>
              <span className={`text-[10px] mt-1 leading-tight text-center ${active ? 'font-semibold' : ''}`}>
                {item.name}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;

